import { TypedDocumentNode, useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Generic type for items that have id and name
export interface PickerItem {
  id: string;
  name: string;
}

// Type for inputs that might be just IDs or full objects
export type PickerInput<T extends PickerItem> = T | string | { id: string };

// Helper function to check if a value is a full item with name
function isFullPickerItem<T extends PickerItem>(item: any): item is T {
  return item && typeof item === "object" && "id" in item && "name" in item;
}

interface UseMultiPickerWithHydrationProps<T extends PickerItem, TData> {
  /** The current value prop from parent */
  value?: (T | PickerInput<T>)[] | null | undefined;
  /** GraphQL query to fetch items by IDs */
  query: TypedDocumentNode<
    TData,
    { search: string; page: number; pageSize: number }
  >;
  /** Function to extract items from query result */
  getItems: (data: TData) => T[];
  /** Search filter format for IDs thats passed to the query, generally it's going to be "ids" */
  idsSearchPrefix: string;
  /** Original onChange callback from parent */
  onChange: (items: T[] | null) => void;
  /** Property to use as cache key (defaults to "id") */
  cacheKey?: keyof T;
}

export function useMultiPickerWithHydration<T extends PickerItem, TData>({
  value,
  query,
  getItems,
  idsSearchPrefix,
  onChange,
  cacheKey = "id" as keyof T,
}: UseMultiPickerWithHydrationProps<T, TData>) {
  // Component-level cache for item data
  const itemCache = useRef<Map<string, T>>(new Map());

  const [resolvedValue, setResolvedValue] = useState<T[]>([]);
  const [isHydrating, setIsHydrating] = useState(false);

  // Extract IDs that need hydration (not in cache)
  const idsToHydrate = useMemo(() => {
    if (!value || !Array.isArray(value)) return [];

    return value
      .filter((item) => {
        if (isFullPickerItem<T>(item)) {
          // Cache full items when we see them
          itemCache.current.set(String(item[cacheKey]), item);
          return false; // Don't need to hydrate
        }

        const itemKeyValue = typeof item === "string" ? item : item?.id;
        if (!itemKeyValue) return false;

        // Check if we already have this in cache
        return !itemCache.current.has(itemKeyValue);
      })
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object" && "id" in item) return item.id;
        return null;
      })
      .filter((id): id is string => id !== null);
  }, [value, cacheKey]);

  // Query to hydrate missing items
  const { data: hydrationData, loading: hydrationLoading } = useQuery(query, {
    variables: {
      search:
        idsToHydrate.length > 0
          ? `${idsSearchPrefix}:${idsToHydrate.join("|")}`
          : "",
      pageSize: idsToHydrate.length || 1,
      page: 0,
    },
    skip: idsToHydrate.length === 0,
  });

  // Update cache when hydration data changes
  useEffect(() => {
    // Add newly hydrated items to cache
    if (hydrationData) {
      const items = getItems(hydrationData);
      items.forEach((item) => {
        itemCache.current.set(String(item[cacheKey]), item);
      });
    }
  }, [hydrationData, getItems, cacheKey]);

  // Resolve the current value using cache - separate effect to prevent clearing during hydration
  useEffect(() => {
    // Don't clear resolved value if we're just waiting for hydration
    if (!value || !Array.isArray(value)) {
      setResolvedValue([]);
      setIsHydrating(false);
      return;
    }

    const resolved: T[] = [];
    let needsHydration = false;

    value.forEach((item) => {
      if (isFullPickerItem<T>(item)) {
        resolved.push(item);
      } else {
        const itemKeyValue = typeof item === "string" ? item : item?.id;
        if (itemKeyValue) {
          const cached = itemCache.current.get(itemKeyValue);
          if (cached) {
            resolved.push(cached);
          } else {
            needsHydration = true;
          }
        }
      }
    });

    // Only update resolved value if we have all items or no hydration is needed
    if (!needsHydration || resolved.length === value.length) {
      setResolvedValue(resolved);
    }

    setIsHydrating(needsHydration && hydrationLoading);
  }, [value, hydrationLoading, cacheKey]);

  // We need to use this enhanced onChange because when the picker changes the value, we actually have
  // the data that we need, so this can save us a hydration call
  const enhancedOnChange = useCallback(
    (selectedValue: T | T[] | null) => {
      const newValue = Array.isArray(selectedValue)
        ? selectedValue
        : selectedValue
        ? [selectedValue]
        : [];

      // Cache newly selected items
      if (newValue.length > 0) {
        newValue.forEach((item) => {
          if (isFullPickerItem<T>(item)) {
            itemCache.current.set(String(item[cacheKey]), item);
          }
        });
      }

      // Update resolved value immediately for responsive UI
      setResolvedValue(newValue);

      // Notify parent of the change (convert empty array back to null for parent)
      onChange(newValue.length > 0 ? newValue : null);
    },
    [onChange, cacheKey]
  );

  // Enhanced getItems function that includes cached items in options
  const enhancedGetItems = useCallback(
    (data: TData) => {
      const searchResults = getItems(data);

      // If we have resolved items, ensure they're included in the options
      if (resolvedValue.length > 0) {
        const searchResultKeys = new Set(
          searchResults.map((item) => String(item[cacheKey]))
        );
        const missingCachedItems = resolvedValue.filter(
          (item) => !searchResultKeys.has(String(item[cacheKey]))
        );

        // Combine search results with any missing cached items
        return [...searchResults, ...missingCachedItems];
      }

      return searchResults;
    },
    [getItems, resolvedValue, cacheKey]
  );

  return {
    resolvedValue,
    isHydrating,
    enhancedOnChange,
    enhancedGetItems,
  };
}
